<script setup>
import { Tippy } from 'vue-tippy';
import ProgressBarCurrentMax from '@/components/Gamification/ProgressBarCurrentMax.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import { IconFileAnalytics, IconThumbDownFilled, IconThumbUpFilled, IconTrash } from '@tabler/icons-vue';
import draggable from 'vuedraggable';
import StudentsProfileStackFiltered from '@/components/StudentsProfileStackFiltered.vue';
import TeamSurveyEvaluation from '@/Pages/Teacher/Gamification/Groups/MainTeams/partials/TeamSurveyEvaluation.vue';
import { computed, onMounted, ref } from 'vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import PageSection from '@/Shared/PageSection.vue';

defineOptions({
    name: 'GroupingTeamsDraft',
});
const props = defineProps({
    allStudents: {
        type: Array,
        required: true,
    },
    withSurvey: {
        type: Boolean,
        default: false,
    },
    id: {
        type: [Number, String],
        default: 0,
    },
    isEdit: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: '',
    },
    description: {
        type: String,
        default: '',
    },
});

const teams = defineModel({ type: Array });

function addTeam() {
    teams.value.push({
        id: null,
        name: null,
        students: [],
    });
}

const deleteTeam = (index) => {
    teams.value.splice(index, 1);
};

const canAddTeam = computed(() => {
    if (props.isEdit) {
        return true;
    }

    return teams.value.length <= props.allStudents.length / 2 + 1;
});

const overAllEvaluation = computed(() => {
    if (!props.withSurvey) {
        return null;
    }

    const evaluation = {
        positive: 0,
        negative: 0,
        total: 0,
    };

    teams.value.forEach((team) => {
        const studentIds = team.students.map((s) => s.id);
        team.students.forEach((student) => {
            const positive = student.positive.filter((p) => studentIds.includes(p));
            const negative = student.negative.filter((n) => studentIds.includes(n));

            evaluation.positive += positive.length;
            evaluation.negative += negative.length;
            evaluation.total += positive.length + negative.length;
        });
    });

    return evaluation;
});

const withoutTeam = ref({
    students: [],
});
onMounted(() => {
    if (props.isEdit && teams.value.length === 0) {
        withoutTeam.value.students = JSON.parse(JSON.stringify(props.allStudents));
        addTeam();
    }
});
</script>

<template>
    <details class="w-full" :open="isEdit">
        <summary>{{ title }}</summary>
        <div class="mt-1 text-sm leading-6 text-gray-500">
            {{ description }}
        </div>
        <div class="mt-5">
            <div class="md:grid md:grid-cols-3 md:gap-6 bg-white">
                <div
                    v-for="(team, index) in teams"
                    class="bg-gray-200 p-2 rounded-lg"
                    style="min-height: 100px"
                    :key="index"
                >
                    <div>
                        <div class="flex items-center gap-4">
                            <FormInput
                                v-model="team.name"
                                name="name"
                                :id="`team_name_${index}`"
                                type="text"
                                max="30"
                                :placeholder="$t('messages.team_name')"
                                class="mt-1 w-full"
                            />

                            <div class="flex gap-1 items-center">
                                <button
                                    type="button"
                                    :title="$t('messages.remove')"
                                    @click="deleteTeam(index)"
                                    v-if="team.students.length === 0"
                                >
                                    <IconTrash class="text-gray-400 w-3 h-3" />
                                </button>
                            </div>
                        </div>

                        <draggable
                            v-model="team.students"
                            :group="`draft_` + id"
                            class="mt-3 md:grid md:grid-cols-2 md:gap-6 h-full list-group"
                            style="min-height: 80px"
                            item-key="id"
                        >
                            <template #item="{ element: student }">
                                <div class="bg-white rounded-xl p-2">
                                    <div class="flex shrink-0 space-x-2 items-center">
                                        <img
                                            :src="student.avatar_or_default"
                                            alt=""
                                            class="max-w-none h-16 w-16 rounded-full ring-2 ring-white"
                                        />
                                        <div>
                                            {{ student.name_or_nickname }}
                                        </div>
                                    </div>
                                    <div v-if="withSurvey" class="flex items-center">
                                        <div>
                                            <Tippy interactive>
                                                <IconFileAnalytics
                                                    class="w-6 h-6 inline-block"
                                                    :class="
                                                        student.positive.length + student.negative.length > 0
                                                            ? 'text-gray-400'
                                                            : 'text-gray-200'
                                                    "
                                                />

                                                <template #content>
                                                    <div :key="student.id" class="mt-3">
                                                        <div>
                                                            {{ student.name_or_nickname }}
                                                        </div>
                                                        <div class="flex gap-2 items-center">
                                                            <div>
                                                                <img
                                                                    :title="student.name_or_nickname"
                                                                    :src="student.avatar_or_default"
                                                                    class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                                                    alt=""
                                                                />
                                                            </div>
                                                            <div>
                                                                <div class="flex gap-1 items-center">
                                                                    <IconThumbUpFilled
                                                                        class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                                        style="color: #68d391"
                                                                    />
                                                                    <StudentsProfileStackFiltered
                                                                        :students="allStudents"
                                                                        :selected="student.positive"
                                                                        class="inline-block h-8"
                                                                    />
                                                                </div>
                                                                <div class="flex gap-1 items-center">
                                                                    <IconThumbDownFilled
                                                                        class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                                        style="color: #fc8181"
                                                                    />
                                                                    <StudentsProfileStackFiltered
                                                                        :students="allStudents"
                                                                        :selected="student.negative"
                                                                        class="inline-block h-8"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Tippy>
                                        </div>
                                        <div>
                                            <Tippy interactive>
                                                <div class="flex gap-1">
                                                    <div
                                                        v-for="i in team.students.filter((s) =>
                                                            student.positive.includes(s.id),
                                                        ).length"
                                                        class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-400"
                                                        :key="i"
                                                    />
                                                    <div
                                                        v-for="i in student.positive.filter(
                                                            (s) => !team.students.map((ts) => ts.id).includes(s),
                                                        )"
                                                        class="shrink-0 w-1.5 h-1.5 rounded-full bg-green-100"
                                                        :key="i"
                                                    />
                                                </div>
                                                <div class="flex gap-1 mt-1">
                                                    <div
                                                        v-for="i in team.students.filter((s) =>
                                                            student.negative.includes(s.id),
                                                        ).length"
                                                        class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-400"
                                                        :key="i"
                                                    />
                                                    <div
                                                        v-for="i in student.negative.filter(
                                                            (s) => !team.students.map((ts) => ts.id).includes(s),
                                                        )"
                                                        class="shrink-0 w-1.5 h-1.5 rounded-full bg-red-100"
                                                        :key="i"
                                                    />
                                                </div>
                                                <template #content>
                                                    <div :key="student.id" class="mt-3">
                                                        <div>
                                                            {{ student.name_or_nickname }}
                                                        </div>
                                                        <div class="flex gap-2 items-center">
                                                            <div>
                                                                <img
                                                                    :title="student.name_or_nickname"
                                                                    :src="student.avatar_or_default"
                                                                    class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                                                                    alt=""
                                                                />
                                                            </div>
                                                            <div>
                                                                <div class="flex gap-1 items-center">
                                                                    <IconThumbUpFilled
                                                                        class="w-8 h-8 text-green-400 mr-3 inline-block"
                                                                        style="color: #68d391"
                                                                    />
                                                                    <StudentsProfileStackFiltered
                                                                        :students="team.students"
                                                                        :selected="student.positive"
                                                                        class="inline-block h-8"
                                                                    />
                                                                </div>
                                                                <div class="flex gap-1 items-center">
                                                                    <IconThumbDownFilled
                                                                        class="w-8 h-8 text-red-400 mr-3 inline-block"
                                                                        style="color: #fc8181"
                                                                    />
                                                                    <StudentsProfileStackFiltered
                                                                        :students="team.students"
                                                                        :selected="student.negative"
                                                                        class="inline-block h-8"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </Tippy>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                    </div>
                    <div><TeamSurveyEvaluation :team="team" /></div>
                </div>
            </div>
        </div>
        <div v-if="isEdit">
            <div v-if="canAddTeam">
                <div>
                    <button @click="addTeam()" style="min-height: 100px" :title="$t('messages.add_team')">
                        <PlusButton />
                    </button>
                </div>
            </div>
            <PageSection id="students_not_in_teams" class="mt-5">
                <template v-slot:title>
                    {{ $t('messages.students_not_in_teams') }}
                </template>
                <div class="mt-5 bg-gray-200 p-4">
                    <draggable
                        class="md:grid md:grid-cols-6 md:gap-6 rounded-lg p-2"
                        style="min-height: 100px"
                        :list="withoutTeam.students"
                        :group="`draft_` + id"
                        item-key="id"
                    >
                        <template #item="{ element: student }">
                            <div class="bg-white rounded-xl p-2" :id="student.id">
                                <div class="flex shrink-0 space-x-2 items-center">
                                    <img
                                        :src="student.avatar_or_default"
                                        alt=""
                                        class="max-w-none h-16 w-16 rounded-full ring-2 ring-white"
                                    />
                                    <div>
                                        {{ student.name_or_nickname }}
                                    </div>
                                </div>
                            </div>
                        </template>
                    </draggable>
                    <div v-if="withoutTeam.students.length === 0" class="text-gray-700">
                        {{ $t('messages.all_students_in_teams') }}
                    </div>
                </div>
            </PageSection>
        </div>
    </details>

    <ProgressBarCurrentMax
        v-if="overAllEvaluation"
        :max="overAllEvaluation.total"
        :current="overAllEvaluation.positive"
        background-color="red"
        class="mt-5"
    >
        {{ overAllEvaluation.positive }} vs -{{ overAllEvaluation.negative }}
    </ProgressBarCurrentMax>
</template>
